/**
 * Browser for the service-mapping store.
 *
 * The model is relational rather than a single tree, so this shows two views:
 * "Places" walks country → region → city → services, and "Providers" lists
 * organizations with their services underneath. Taxonomy and geography records
 * are reachable from the flat lists at the bottom.
 */

import { useState, useCallback, useMemo, useEffect, useContext } from "react"
import { PendingDot, PendingIdsContext } from "../shared/components/PendingChanges"
import { FilterBar, matchesWorkflow, workflowFilterDefs, type FilterDef, type FilterValue, type RecordInfo } from "../shared/components/RecordFilters"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  ChevronRight, ChevronDown, Plus, Trash2, Search, RefreshCw, ArrowUpDown,
  MapPin, Building2, Globe, Map as MapIcon, Landmark, Tag, Users, Accessibility as AccessIcon,
} from "lucide-react"
import { TreeRow } from "../shared/components/TreeRow"
import { coverageOf, describeLanguage, translationStatus, STATUS_LABEL, type LanguageInfo } from "../shared/languages"
import { StatusDot } from "../shared/components/languages/StatusDot"
import { CoverageLine } from "../shared/components/languages/CoverageLine"
import { serviceMapAdapter } from "../shared/localeSyncAdapters"
import * as db from "./store"
import {
  type ServiceStore, type EntityKind, BUCKET, displayName, SERVICE_STATUSES, serviceStatusFor,
} from "./types"
import { ServiceMap } from "./ServiceMap"

interface ServiceBrowserProps {
  store: ServiceStore
  locale: string
  language?: LanguageInfo
  selected: { kind: EntityKind; id: string } | null
  onSelect: (kind: EntityKind, id: string) => void
  onCreate: (kind: EntityKind, fields: Record<string, any>) => void
  onDelete: (kind: EntityKind, id: string) => void
  // False for view-only roles: no ways to add anything are shown
  canEdit?: boolean
  // Unpublished changes, review dates and owners, for the filters
  recordInfo?: (kind: EntityKind, id: string) => RecordInfo | undefined
  people?: { id: string; name: string }[]
}

const STATUS_TONE: Record<string, string> = {
  published: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
  draft: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
  archived: "bg-muted text-muted-foreground",
  private: "bg-muted text-muted-foreground",
}

const SORT_KEY = "svc-cms-sort"
const VIEW_KEY = "svc-cms-view"

type ViewKey = "services" | "places" | "providers" | "taxonomy" | "map"

type SortKey = "name" | "name-desc" | "updated" | "created" | "created-asc" | "status"

const SORT_LABELS: Record<SortKey, string> = {
  "name": "A → Z",
  "name-desc": "Z → A",
  "updated": "Recently updated",
  "created": "Newest first",
  "created-asc": "Oldest first",
  "status": "Status",
}

/** Ordering services put drafts and incomplete records first, where they need attention. */
/**
 * Compact relative age, e.g. "3d ago".
 *
 * Short by design — these sit in a dense list where a full timestamp would
 * dominate the row. The exact time is on the title attribute.
 */
function relativeTime(ts?: number): string {
  if (!ts) return ""
  const secs = Math.max(0, Math.round((Date.now() - ts) / 1000))
  if (secs < 60) return "just now"
  const mins = Math.round(secs / 60)
  if (mins < 60) return `${mins}m ago`
  const hours = Math.round(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.round(hours / 24)
  if (days < 30) return `${days}d ago`
  const months = Math.round(days / 30)
  if (months < 12) return `${months}mo ago`
  return `${Math.round(months / 12)}y ago`
}

const STATUS_ORDER = ["draft", "ready for translation", "Translated", "published", "archived", "private"]

export function ServiceBrowser({
  store, locale, language = describeLanguage(locale), selected, onSelect, onCreate, onDelete, canEdit = true,
  recordInfo, people = [],
}: ServiceBrowserProps) {
  const pendingIds = useContext(PendingIdsContext)
  // Add buttons only for roles that can create
  const addable = (fn: () => void) => (canEdit ? fn : undefined)
  const [view, setView] = useState<ViewKey>(
    () => (localStorage.getItem(VIEW_KEY) as ViewKey) || "services",
  )
  useEffect(() => { localStorage.setItem(VIEW_KEY, view) }, [view])
  const [filters, setFilters] = useState<FilterValue>({})
  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [query, setQuery] = useState("")
  const [sort, setSort] = useState<SortKey>(
    () => (localStorage.getItem(SORT_KEY) as SortKey) || "name",
  )
  useEffect(() => { localStorage.setItem(SORT_KEY, sort) }, [sort])

  const toggle = useCallback((key: string) => {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }))
  }, [])

  const name = useCallback(
    (kind: EntityKind, id: string) => {
      const e = db.getEntity(store, kind, id)
      return e ? displayName(e, locale, store.defaultLocale) : id
    },
    [store, locale],
  )

  const isSel = (kind: EntityKind, id: string) => selected?.kind === kind && selected?.id === id

  /**
   * Sorts a list of entities by the active key.
   *
   * Names are compared with localeCompare so accented and non-Latin scripts
   * order correctly rather than by code point. Returns a new array — callers
   * pass store-owned lists that must not be mutated.
   */
  const sorted = useCallback(
    (rows: any[]) => {
      const byName = (a: any, b: any) =>
        displayName(a, locale, store.defaultLocale)
          .localeCompare(displayName(b, locale, store.defaultLocale), locale, { sensitivity: "base" })
      const copy = [...rows]
      switch (sort) {
        case "name-desc": return copy.sort((a, b) => byName(b, a))
        case "updated": return copy.sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
        case "created": return copy.sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0))
        case "created-asc": return copy.sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0))
        case "status": return copy.sort((a, b) => {
          const rank = (x: any) => {
            const i = STATUS_ORDER.indexOf(x.status ? serviceStatusFor(x, locale) : x.status)
            // Entities with no status sort after those that have one.
            return i === -1 ? STATUS_ORDER.length : i
          }
          return rank(a) - rank(b) || byName(a, b)
        })
        default: return copy.sort(byName)
      }
    },
    [sort, locale, store.defaultLocale],
  )

  // Each record's state in the working language
  const statusOf = useCallback(
    (kind: EntityKind, id: string) => {
      const e = db.getEntity(store, kind, id)
      return e ? translationStatus(e.translations, locale, store.defaultLocale) : undefined
    },
    [store, locale],
  )
  const statusTitle = (kind: EntityKind, id: string) => {
    const st = statusOf(kind, id)
    return st ? `${language.name}: ${STATUS_LABEL[st]}` : undefined
  }
  const coverage = useMemo(
    () => coverageOf(serviceMapAdapter.sources(store).map(x => x.record), locale, store.defaultLocale),
    [store, locale],
  )

  // Search short-circuits the tree entirely — when the user is looking for
  // something specific, hierarchy is noise.
  const hits = useMemo(() => {
    if (query.trim().length < 2) return null
    return db.searchStore(store, query, { limit: 60 })
  }, [store, query])


  const statusBadge = (status?: string) =>
    status ? (
      <Badge variant="secondary" className={cn("shrink-0 text-[10px] px-1 py-0", STATUS_TONE[status] ?? "")}>
        {status === "ready for translation" ? "for translation" : status}
      </Badge>
    ) : null

  const serviceRows = (services: ReturnType<typeof db.listEntities>, depth: number) =>
    services.map((s: any) => (
      <TreeRow
        selected={isSel("service", s.id)} onSelect={onSelect} onDelete={onDelete}
        status={statusOf("service", s.id)} statusTitle={statusTitle("service", s.id)}
        key={s.id}
        kind="service"
        id={s.id}
        label={displayName(s, locale, store.defaultLocale)}
        icon={<MapPin className="h-3.5 w-3.5" />}
        depth={depth}
        badge={statusBadge(serviceStatusFor(s, locale))}
      />
    ))

  /**
   * Services bucketed by where they hang in the geography tree.
   *
   * The tree previously ran a full `Object.values(store.services).filter(...)`
   * for EVERY country, region and city it drew — so a dataset with C countries,
   * R regions and T cities did (C + R + T) full passes over every service.
   * One pass building three maps replaces all of them.
   */
  const geoIndex = useMemo(() => {
    const countryOrphans = new Map<string, any[]>()   // in a country, no region
    const regionOrphans = new Map<string, any[]>()    // in a region, no city
    const byCity = new Map<string, any[]>()
    const push = (m: Map<string, any[]>, k: string, v: any) => {
      const list = m.get(k)
      if (list) list.push(v)
      else m.set(k, [v])
    }
    // Conditions mirror the three filters this replaced EXACTLY, rather than
    // being an if/else chain. They are not mutually exclusive: a service with a
    // country and a city but no region is both a country orphan and a city
    // child, and it appeared in both places before. Collapsing that into
    // first-match-wins would silently change what the tree shows.
    for (const s of Object.values(store.services) as any[]) {
      if (s.countryId && !s.regionId) push(countryOrphans, s.countryId, s)
      if (s.regionId && !s.cityId) push(regionOrphans, s.regionId, s)
      if (s.cityId) push(byCity, s.cityId, s)
    }
    return { countryOrphans, regionOrphans, byCity }
  }, [store.services])

  // ── Places: country → region → city → services ───────────────────────────
  const placesView = () => {
    const countries = sorted(db.listEntities(store, "country"))
    if (!countries.length) {
      return <Empty label="No countries yet" onAdd={addable(() => onCreate("country", { name: "New country" }))} addLabel="Add country" />
    }
    const rows = countries.map((c: any) => {
      const openC = expanded[`c:${c.id}`]
      const regions = sorted(db.regionsOfCountry(store, c.id))
      // Services with no region would otherwise be invisible in this tree.
      const orphans = sorted(geoIndex.countryOrphans.get(c.id) ?? [])
      return (
        <div key={c.id}>
          <TreeRow
            selected={isSel("country", c.id)} onSelect={onSelect} onDelete={onDelete}
            status={statusOf("country", c.id)} statusTitle={statusTitle("country", c.id)}
            kind="country" id={c.id} depth={0}
            label={displayName(c, locale, store.defaultLocale)}
            icon={<Globe className="h-3.5 w-3.5" />}
            expandable isOpen={openC} onToggle={() => toggle(`c:${c.id}`)}
            onAdd={addable(() => onCreate("region", { name: "New region", countryId: c.id }))}
            addTitle="Add region"
            badge={<span className="shrink-0 text-[10px] text-muted-foreground">{regions.length}</span>}
          />
          {openC && regions.map((r: any) => {
            const openR = expanded[`r:${r.id}`]
            const cities = sorted(db.citiesOfRegion(store, r.id))
            const regionOrphans = sorted(geoIndex.regionOrphans.get(r.id) ?? [])
            return (
              <div key={r.id}>
                <TreeRow
                  selected={isSel("region", r.id)} onSelect={onSelect} onDelete={onDelete}
                  status={statusOf("region", r.id)} statusTitle={statusTitle("region", r.id)}
                  kind="region" id={r.id} depth={1}
                  label={displayName(r, locale, store.defaultLocale)}
                  icon={<MapIcon className="h-3.5 w-3.5" />}
                  expandable isOpen={openR} onToggle={() => toggle(`r:${r.id}`)}
                  onAdd={addable(() => onCreate("city", { name: "New city", countryId: c.id, regionId: r.id }))}
                  addTitle="Add city"
                />
                {openR && cities.map((ct: any) => {
                  const openCt = expanded[`ct:${ct.id}`]
                  const svcs = sorted(geoIndex.byCity.get(ct.id) ?? [])
                  return (
                    <div key={ct.id}>
                      <TreeRow
                        selected={isSel("city", ct.id)} onSelect={onSelect} onDelete={onDelete}
                        status={statusOf("city", ct.id)} statusTitle={statusTitle("city", ct.id)}
                        kind="city" id={ct.id} depth={2}
                        label={displayName(ct, locale, store.defaultLocale)}
                        icon={<Landmark className="h-3.5 w-3.5" />}
                        expandable isOpen={openCt} onToggle={() => toggle(`ct:${ct.id}`)}
                        onAdd={addable(() => onCreate("service", {
                          name: "New service", countryId: c.id, regionId: r.id, cityId: ct.id, status: "draft",
                        }))}
                        addTitle="Add service"
                        badge={<span className="shrink-0 text-[10px] text-muted-foreground">{svcs.length}</span>}
                      />
                      {openCt && serviceRows(svcs, 3)}
                    </div>
                  )
                })}
                {openR && serviceRows(regionOrphans, 2)}
              </div>
            )
          })}
          {openC && serviceRows(orphans, 1)}
        </div>
      )
    })
    return <>{rows}{canEdit && <AddRow label="Add country" onClick={() => onCreate("country", { name: "New country" })} />}</>
  }

  /**
   * Every service in one flat list.
   *
   * The hierarchy views only sort within a parent, so this is the view where
   * sorting applies across the whole dataset — the reason it exists.
   */
  const servicesView = () => {
    let rows = Object.values(store.services) as any[]
    const f = filters
    // Status is the viewed language's, matching what the badges show
    if (f.status) rows = rows.filter(s => serviceStatusFor(s, locale) === f.status)
    if (f.country) rows = rows.filter(s => s.countryId === f.country)
    if (f.category) rows = rows.filter(s => (s.categoryIds ?? []).includes(f.category))
    if (f.provider) rows = rows.filter(s => (f.provider === "none" ? !s.providerId : s.providerId === f.provider))
    if (f.translation) rows = rows.filter(s => translationStatus(s.translations, locale, store.defaultLocale) === f.translation)
    rows = rows.filter(s => matchesWorkflow(recordInfo?.("service", s.id), f))
    rows = sorted(rows)

    if (!rows.length) {
      return (
        <p className="p-4 text-center text-sm text-muted-foreground">
          {Object.keys(f).length ? "No services match these filters." : "No services yet."}
        </p>
      )
    }

    return rows.map((s: any) => {
      const provider = s.providerId ? name("provider", s.providerId) : null
      const place = db.servicePath(store, s.id, locale).split(" / ").slice(0, -1).join(" / ")
      return (
        <div
          key={s.id}
          className={cn(
            "group flex cursor-pointer items-start gap-2 rounded px-2 py-1.5",
            isSel("service", s.id) ? "bg-primary/10" : "hover:bg-muted/60",
          )}
          onClick={() => onSelect("service", s.id)}
        >
          <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <span className="truncate text-sm">{displayName(s, locale, store.defaultLocale)}</span>
              {pendingIds.has(s.id) && <PendingDot />}
              {(() => {
                const st = statusOf("service", s.id)
                return st === "missing" || st === "unreviewed" ? <StatusDot status={st} title={statusTitle("service", s.id)} /> : null
              })()}
            </div>
            {(provider || place) && (
              <p className="truncate text-[11px] text-muted-foreground">
                {[provider, place].filter(Boolean).join(" · ")}
              </p>
            )}
          </div>
          {typeof s.lat !== "number" && (
            <span className="mt-0.5 hidden shrink-0 text-[10px] text-muted-foreground @xs:inline" title="No map location">no pin</span>
          )}
          {s.updatedAt && (
            <span
              className="mt-0.5 hidden shrink-0 text-[10px] text-muted-foreground @xs:inline"
              title={`Last updated ${new Date(s.updatedAt).toLocaleString()}`}
            >
              {relativeTime(s.updatedAt)}
            </span>
          )}
          {statusBadge(serviceStatusFor(s, locale))}
          <button
            type="button"
            className="mt-0.5 shrink-0 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
            title="Delete service"
            onClick={e => { e.stopPropagation(); onDelete("service", s.id) }}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      )
    })
  }

  // ── Providers: organization → its services ───────────────────────────────
  const providersView = () => {
    const providers = sorted(db.listEntities(store, "provider"))
    if (!providers.length) {
      return <Empty label="No providers yet" onAdd={addable(() => onCreate("provider", newProvider()))} addLabel="Add provider" />
    }
    const rows = providers.map((p: any) => {
      const open = expanded[`p:${p.id}`]
      const svcs = sorted(db.servicesOfProvider(store, p.id))
      return (
        <div key={p.id}>
          <TreeRow
            selected={isSel("provider", p.id)} onSelect={onSelect} onDelete={onDelete}
            status={statusOf("provider", p.id)} statusTitle={statusTitle("provider", p.id)}
            kind="provider" id={p.id} depth={0}
            label={displayName(p, locale, store.defaultLocale)}
            icon={<Building2 className="h-3.5 w-3.5" />}
            expandable isOpen={open} onToggle={() => toggle(`p:${p.id}`)}
            onAdd={addable(() => onCreate("service", {
              name: "New service", providerId: p.id, countryId: p.countryId, status: "draft",
            }))}
            addTitle="Add service"
            badge={<span className="shrink-0 text-[10px] text-muted-foreground">{svcs.length}</span>}
          />
          {open && serviceRows(svcs, 1)}
        </div>
      )
    })
    return <>{rows}{canEdit && <AddRow label="Add provider" onClick={() => onCreate("provider", newProvider())} />}</>
  }

  // ── Taxonomy: flat lists per kind ────────────────────────────────────────
  const TAXONOMY: Array<{ kind: EntityKind; label: string; icon: React.ReactNode }> = [
    { kind: "serviceCategory", label: "Service categories", icon: <Tag className="h-3.5 w-3.5" /> },
    { kind: "tag", label: "Tags", icon: <Tag className="h-3.5 w-3.5" /> },
    { kind: "providerCategory", label: "Provider categories", icon: <Building2 className="h-3.5 w-3.5" /> },
    { kind: "population", label: "Populations served", icon: <Users className="h-3.5 w-3.5" /> },
    { kind: "accessibility", label: "Accessibility", icon: <AccessIcon className="h-3.5 w-3.5" /> },
  ]

  const taxonomyView = () =>
    TAXONOMY.map(({ kind, label, icon }) => {
      const open = expanded[`t:${kind}`]
      const rows = sorted(db.listEntities(store, kind))
      return (
        <div key={kind}>
          <div
            className="group flex items-center gap-1 rounded px-1 py-1 text-sm cursor-pointer hover:bg-muted/60"
            onClick={() => toggle(`t:${kind}`)}
          >
            {open ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
            <span className="shrink-0 text-muted-foreground">{icon}</span>
            <span className="truncate flex-1 font-medium">{label}</span>
            <span className="shrink-0 text-[10px] text-muted-foreground">{rows.length}</span>
            {canEdit && (
              <button
                type="button"
                className="shrink-0 text-muted-foreground/60 hover:text-foreground"
                title={`Add to ${label.toLowerCase()}`}
                onClick={e => { e.stopPropagation(); onCreate(kind, { name: `New ${NEW_NAME[kind] ?? "entry"}` }) }}
              >
                <Plus className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
          {open && rows.map((r: any) => (
            <TreeRow
              selected={isSel(kind, r.id)} onSelect={onSelect} onDelete={onDelete}
              status={statusOf(kind, r.id)} statusTitle={statusTitle(kind, r.id)}
              key={r.id} kind={kind} id={r.id} depth={1}
              label={displayName(r, locale, store.defaultLocale)}
              icon={icon}
            />
          ))}
        </div>
      )
    })

  const named = (kind: EntityKind) => sorted(db.listEntities(store, kind)).map((e: any) => ({ value: e.id, label: displayName(e, locale, store.defaultLocale) }))
  const filterDefs: FilterDef[] = [
    { key: "status", label: "Status", options: SERVICE_STATUSES.map(st => ({ value: st, label: st.charAt(0).toUpperCase() + st.slice(1) })) },
    { key: "country", label: "Country", options: named("country") },
    { key: "category", label: "Category", options: named("serviceCategory") },
    { key: "provider", label: "Provider", options: [{ value: "none", label: "No provider" }, ...named("provider")] },
    { key: "translation", label: language.name, options: [
      { value: "missing", label: "Missing" }, { value: "unreviewed", label: "Needs review" }, { value: "reviewed", label: "Reviewed" },
    ] },
    ...workflowFilterDefs(people),
  ]

  // Where a new record goes: the place and provider of whatever is selected
  const place = useMemo(() => {
    const out: { countryId?: string; regionId?: string; cityId?: string; providerId?: string } = {}
    const e: any = selected ? (store as any)[BUCKET[selected.kind]]?.[selected.id] : null
    if (!e) return out
    if (selected!.kind === "country") out.countryId = e.id
    if (selected!.kind === "region") Object.assign(out, { countryId: e.countryId, regionId: e.id })
    if (selected!.kind === "city") Object.assign(out, { countryId: e.countryId, regionId: e.regionId, cityId: e.id })
    if (selected!.kind === "provider") Object.assign(out, { countryId: e.countryId, providerId: e.id })
    if (selected!.kind === "service") Object.assign(out, { countryId: e.countryId, regionId: e.regionId, cityId: e.cityId, providerId: e.providerId })
    return out
  }, [selected, store])
  // With one country there's no question which a new region or provider belongs to
  const countryIds = Object.keys(store.countries)
  const defaultCountry = place.countryId ?? (countryIds.length === 1 ? countryIds[0] : undefined)
  const given = (fields: Record<string, any>) =>
    Object.fromEntries(Object.entries(fields).filter(([, v]) => v !== undefined && v !== null && v !== ""))
  function newProvider() { return given({ name: "New provider", countryId: defaultCountry }) }
  const regionCountry = defaultCountry

  return (
    // A container, so rows drop their small labels when the tree is narrow
    <div className="@container flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-1 border-b p-2">
        {canEdit && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="h-8 gap-1 px-2 text-xs" title="Create a service, provider, place or category">
                <Plus className="h-3.5 w-3.5" /> New
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              <DropdownMenuItem onSelect={() => onCreate("service", given({ name: "New service", status: "draft", ...place }))}>
                <MapPin className="h-3.5 w-3.5" /> Service
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => onCreate("provider", newProvider())}>
                <Building2 className="h-3.5 w-3.5" /> Provider
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] font-normal text-muted-foreground">Places</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => onCreate("country", { name: "New country" })}>
                <Globe className="h-3.5 w-3.5" /> Country
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!regionCountry}
                title={regionCountry ? undefined : "Select a country first"}
                onSelect={() => onCreate("region", { name: "New region", countryId: regionCountry })}
              >
                <MapIcon className="h-3.5 w-3.5" /> Region
                {!regionCountry && <span className="ml-auto text-[10px] text-muted-foreground">select a country</span>}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={!place.regionId}
                onSelect={() => onCreate("city", { name: "New city", countryId: place.countryId, regionId: place.regionId })}
              >
                <Landmark className="h-3.5 w-3.5" /> City
                {!place.regionId && <span className="ml-auto text-[10px] text-muted-foreground">select a region</span>}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-[10px] font-normal text-muted-foreground">Taxonomy</DropdownMenuLabel>
              {TAXONOMY.map(({ kind, label, icon }) => (
                <DropdownMenuItem key={kind} onSelect={() => onCreate(kind, { name: `New ${NEW_NAME[kind] ?? "entry"}` })}>
                  {icon} {label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        <div className="relative min-w-[140px] flex-1">
          <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search services, providers, places…"
            className="h-8 pl-7 text-sm"
          />
        </div>
        <Select value={sort} onValueChange={v => setSort(v as SortKey)}>
          <SelectTrigger className="h-8 w-[150px] gap-1.5 text-xs" title="Sort order">
            <ArrowUpDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            {/* Takes the free space so the label stays on the icon's line
                rather than being squeezed by justify-between. */}
            <SelectValue className="flex-1 truncate text-left" />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(SORT_LABELS) as SortKey[]).map(k => (
              <SelectItem key={k} value={k} className="text-xs">{SORT_LABELS[k]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {!hits && (
        <div className="flex flex-wrap gap-1 border-b px-2 py-1.5">
          {([
            ["services", "Services"],
            ["places", "Places"],
            ["providers", "Providers"],
            ["taxonomy", "Taxonomy"],
            ["map", "Map"],
          ] as const).map(([v, label]) => (
            <button
              type="button"
              key={v}
              onClick={() => setView(v)}
              className={cn(
                "rounded px-2 py-1 text-xs transition-colors",
                view === v ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {!hits && view === "services" && (
        <FilterBar section="services" defs={filterDefs} value={filters} onChange={setFilters} />
      )}

      {view === "map" && !hits ? (
        <div className="flex-1 overflow-hidden p-2">
          <ServiceMap
            store={store}
            locale={locale}
            selectedId={selected?.kind === "service" ? selected.id : null}
            onSelect={onSelect}
            height="100%"
            className="flex h-full flex-col"
          />
        </div>
      ) : (
      <ScrollArea className="flex-1 min-h-0 [&_[data-slot=scroll-area-viewport]>:first-child]:block!">
        <div className="p-1">
          {hits ? (
            hits.length === 0 ? (
              <p className="p-3 text-sm text-muted-foreground">No matches for “{query}”.</p>
            ) : (
              hits.map(h => (
                <div
                  key={`${h.kind}:${h.id}`}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm",
                    isSel(h.kind, h.id) ? "bg-primary/10" : "hover:bg-muted/60",
                  )}
                  onClick={() => onSelect(h.kind, h.id)}
                >
                  <span className="truncate flex-1">{h.name}</span>
                  <Badge variant="outline" className="shrink-0 text-[10px] px-1 py-0">{h.kind}</Badge>
                </div>
              ))
            )
          ) : view === "services" ? servicesView()
            : view === "places" ? placesView()
            : view === "providers" ? providersView()
            : taxonomyView()}
        </div>
      </ScrollArea>
      )}

      <div className="border-t px-3 py-1.5 text-[11px] text-muted-foreground">
        {(() => {
          const st = db.stats(store)
          return `${st.services} services · ${st.providers} providers · ${st.cities} cities`
        })()}
      </div>
      <CoverageLine language={language} isSource={locale === store.defaultLocale} coverage={coverage} noun="records" />
    </div>
  )
}

function Empty({ label, onAdd, addLabel }: { label: string; onAdd?: () => void; addLabel: string }) {
  return (
    <div className="p-4 text-center">
      <p className="mb-2 text-sm text-muted-foreground">{label}</p>
      {onAdd && (
        <Button variant="outline" size="sm" onClick={onAdd}>
          <Plus className="mr-1 h-3.5 w-3.5" /> {addLabel}
        </Button>
      )}
    </div>
  )
}

// The last row of a list, for adding another
function AddRow({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-1 flex w-full items-center gap-1.5 rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted/60 hover:text-foreground"
    >
      <Plus className="h-3.5 w-3.5" /> {label}
    </button>
  )
}

// What a new taxonomy entry is called until it's renamed
const NEW_NAME: Partial<Record<EntityKind, string>> = {
  serviceCategory: "service category", tag: "tag", providerCategory: "provider category",
  population: "population", accessibility: "accessibility option",
}
